/**
 * Role identifiers — single source of truth across the app.
 *
 * `god` is the Auth/Firestore claim for the platform super admin.
 * When that user authenticates, God Mode (Modo Dios) is active in the UI.
 */
export const ROLES = {
  GOD: 'god',
  BANKER: 'banker',
  LISTER: 'lister',
  RUNNER: 'runner',
  SUPPORT_AGENT: 'support_agent',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

/** Product / display id for the platform god role (claim value remains `god`). */
export const SUPER_ADMIN_ROLE_ID = 'super_admin';

/** True when the session is platform super admin (God Mode). */
export function isGodModeRole(role: string | null | undefined): boolean {
  return role === ROLES.GOD;
}

/** Max items in the bottom tab bar, including the overflow “…” control. */
export const BOTTOM_TAB_BAR_MAX = 5;
export const BOTTOM_TAB_MORE_ID = 'more';

export interface RoleMeta {
  labelKey: string;
  tabs: string[];
  /** Primary tabs in the bar (max 4); remaining tabs appear under “…”. */
  primaryTabs?: string[];
}

export const ROLE_META: Record<Role, RoleMeta> = {
  [ROLES.GOD]: {
    labelKey: 'role_god',
    tabs: ['overview', 'houses', 'users', 'tickets', 'results', 'knowledge', 'audit', 'integrity', 'billing', 'config', 'rates'],
    primaryTabs: ['overview', 'houses', 'users', 'tickets'],
  },
  [ROLES.BANKER]: {
    labelKey: 'role_banker',
    tabs: ['overview', 'newplay', 'plays', 'results', 'users', 'route', 'config', 'rates'],
    primaryTabs: ['overview', 'newplay', 'plays', 'results'],
  },
  [ROLES.LISTER]: {
    labelKey: 'role_lister',
    tabs: ['overview', 'newplay', 'plays', 'results', 'handoff', 'rates'],
    primaryTabs: ['overview', 'newplay', 'plays', 'results'],
  },
  [ROLES.RUNNER]: {
    labelKey: 'role_runner',
    tabs: ['overview', 'route', 'history'],
  },
  [ROLES.SUPPORT_AGENT]: {
    labelKey: 'role_support_agent',
    tabs: ['overview', 'tickets', 'knowledge', 'audit', 'analytics'],
  },
};

export const ASSIGNABLE_ROLES: Role[] = [
  ROLES.BANKER,
  ROLES.LISTER,
  ROLES.RUNNER,
  ROLES.SUPPORT_AGENT,
];

export function getBottomTabLayout(role: Role, visibleTabs?: string[]): {
  primary: string[];
  overflow: string[];
  hasOverflow: boolean;
} {
  const meta = ROLE_META[role];
  const all = visibleTabs ?? meta.tabs;
  const configured = meta.primaryTabs;

  if (!configured?.length || configured.length >= all.length) {
    if (all.length <= BOTTOM_TAB_BAR_MAX) {
      return { primary: all, overflow: [], hasOverflow: false };
    }
    return {
      primary: all.slice(0, BOTTOM_TAB_BAR_MAX - 1),
      overflow: all.slice(BOTTOM_TAB_BAR_MAX - 1),
      hasOverflow: true,
    };
  }

  const primary = configured.filter((id) => all.includes(id));
  const overflow = all.filter((id) => !primary.includes(id));

  // If house-scope / remote filters removed most primaries, fill from remaining tabs.
  if (primary.length === 0 && all.length > 0) {
    if (all.length <= BOTTOM_TAB_BAR_MAX) {
      return { primary: all, overflow: [], hasOverflow: false };
    }
    return {
      primary: all.slice(0, BOTTOM_TAB_BAR_MAX - 1),
      overflow: all.slice(BOTTOM_TAB_BAR_MAX - 1),
      hasOverflow: true,
    };
  }

  return {
    primary,
    overflow,
    hasOverflow: overflow.length > 0,
  };
}
